import { useEffect, useState } from 'react'

import { ChainId } from '@src/configs/chainInfo'
import { ResultCode } from '@src/enums'
import {
    BACKOFF_MULTIPLIER,
    INITIAL_POLLING_INTERVAL,
    MAXIMUM_RETRY_COUNT,
    MAX_POLLING_INTERVAL,
} from '@src/hooks/useWaitForTransaction'
import { exchangeLogger } from '@src/utils/logger'
import { getTronTransactionReceipt } from '@src/utils/tron/utils'
import { useHistoryStore } from '@src/zustand_stores/history.store'

export const useWaitForTransactionTron = () => {
    const [retryCount, setRetryCount] = useState<number>(0)
    const [transactionHash, setTransactionHash] = useState<string>('')
    const [transactionStatus, setTransactionStatus] = useState({ isSuccess: false, isError: false })
    const updateHistory = useHistoryStore(state => state.updateHistory)

    const waitForTransactionReceipt = async (hash: string) => {
        return new Promise((resolve, reject) => {
            setTransactionHash(hash)

            const attemptGetReceipt = async () => {
                if (retryCount >= MAXIMUM_RETRY_COUNT) {
                    setTransactionStatus({ isSuccess: false, isError: true })
                    updateHistory(null, {
                        result: {
                            sourceTxHash: ``,
                            message: 'We are so sorry that this order failed. Please try again.',
                            status: ResultCode.failure,
                        },
                    })
                    reject({ message: 'Exceeded maximum retry count' })
                    return
                }

                try {
                    const response = await getTronTransactionReceipt(hash)
                    if (Object.keys(response).length !== 0 && !!response) {
                        const receipt = {
                            blockHash: response.id,
                            blockNumber: BigInt(response.blockNumber),
                            contractAddress: response.contract_address,
                            cumulativeGasUsed: BigInt(
                                response.receipt.energy_usage || response.receipt.energy_usage_total
                            ),
                            effectiveGasPrice: BigInt(
                                response.receipt.energy_usage || response.receipt.energy_usage_total
                            ),
                            from: '0x',
                            gasUsed: BigInt(response.fee),
                            logs: response.log,
                            logsBloom: '0x',
                            status: response.receipt.result,
                            to: null,
                            transactionHash: response.id,
                            transactionIndex: 0,
                            type: 'legacy',
                            chainId: ChainId.Tron,
                        }
                        resolve(receipt)
                        return
                    }

                    const nextInterval = Math.min(
                        INITIAL_POLLING_INTERVAL * Math.pow(BACKOFF_MULTIPLIER, retryCount),
                        MAX_POLLING_INTERVAL
                    )
                    setRetryCount(prev => prev + 1)
                    setTimeout(attemptGetReceipt, nextInterval)
                } catch (error) {
                    exchangeLogger.error('getTronTransactionReceipt error:', { error })
                    const nextInterval = Math.min(
                        INITIAL_POLLING_INTERVAL * Math.pow(BACKOFF_MULTIPLIER, retryCount),
                        MAX_POLLING_INTERVAL
                    )
                    setRetryCount(prev => prev + 1)
                    setTimeout(attemptGetReceipt, nextInterval)
                }
            }

            attemptGetReceipt()
        })
    }

    useEffect(() => {
        setRetryCount(0)
    }, [transactionHash])

    return { waitForTransactionReceipt }
}
